import { createSlice } from "@reduxjs/toolkit";
import {
  // Admin Management
  createAdminThunk,
  deleteAdminThunk,
  updateAdminPasswordThunk,
  resetPasswordThunk,
  requestPasswordResetThunk,
  getAdminThunk,
  getMyDetailsThunk,
  fetchAdminsThunk,
  toggleBlockAdminThunk,
  getForgotPasswordRequestsThunk,
  completeInitialSetupThunk,
  updateAdminBranchAccessThunk,

  // Status Management
  getAdminStatusThunk,
  updateMyStatusThunk,
  getAvailableAdminsThunk,

  // Availability Management
  getMyAvailabilityThunk,
  updateWorkingHoursThunk,
  addTimeOffThunk,
  removeTimeOffThunk,
  addSpecialHoursThunk,

  // Appointment Settings
  updateAppointmentSettingsThunk,
  toggleAcceptingAppointmentsThunk,

  // Slot Management
  getAvailableSlotsThunk,
  getAvailableSlotsRangeThunk,

  // Notification Settings
  updateNotificationSettingsThunk,

  // Search & Filter
  getAdminsBySpecializationThunk,

  // Integration
  getAdminDetailsThunk,
  checkAdminAvailabilityThunk,
  getBranchAdminsThunk,
  getAdminsByRoleThunk,
  getBulkAdminAvailabilityThunk,
  updateAdminRatingThunk,
  updateAppointmentStatsThunk,

  // Branch Access Management
  getAdminBranchAccessThunk,
  addBranchAccessThunk,
  updateBranchAccessPermissionsThunk,
  removeBranchAccessThunk,
  setAdminPrimaryBranchThunk,
  getAdminsByBranchThunk,
  bulkAssignBranchAccessThunk,

  // Actions
  clearAdminError,
  resetSelectedAdmin,
  setAdminFilter,
  clearAvailabilityData,
  clearSlotData,
  clearBranchAccessData,
} from "../thunks/adminThunks";

const initialState = {
  // Admin data
  admins: [],
  selectedAdmin: null,
  currentAdmin: null,
  forgotPasswordRequests: [],
  availableAdmins: [],
  branchAdmins: [],
  specializedAdmins: [],

  // Status data
  adminStatusMap: {},
  myCurrentStatus: null,

  // Availability data
  myAvailability: null,
  workingHours: null,
  timeOffList: [],
  specialHoursList: [],
  acceptingAppointments: true,

  // Appointment data
  appointmentSettings: null,
  appointmentStats: null,

  // Slot data
  availableSlots: [],
  availableSlotsRange: {},
  selectedSlotDate: null,

  // Notification settings
  notificationSettings: null,

  // Setup data
  setupStatus: null,
  setupSteps: [],
  requiresInitialSetup: false,

  // Branch access
  branchAccessList: [],
  selectedAdminBranchAccess: null,
  branchAdminsList: [],

  // Bulk availability results
  bulkAvailabilityResults: {},

  // Filters
  filters: {
    search: "",
    role: "",
    isBlocked: null,
    branch: "",
    specialization: "",
    appointmentType: "",
  },

  // Loading states
  loading: false,
  loadingList: false,
  loadingSetup: false,
  loadingAvailability: false,
  loadingSlots: false,
  loadingStatus: false,
  loadingBranchAccess: false,

  // Error handling
  error: null,
  successMessage: null,
};

// ========================================
// CONSTANTS - Moved outside initialState
// ========================================
export const ADMIN_CONSTANTS = {
  roles: [
    { value: "root", label: "Root Admin" },
    { value: "admin", label: "Admin" },
    { value: "branch_manager", label: "Branch Manager" },
    { value: "rate_manager", label: "Rate Manager" },
    { value: "staff", label: "Staff" },
  ],

  statusOptions: [
    { value: "AVAILABLE", label: "Available", color: "green" },
    { value: "BUSY", label: "Busy", color: "yellow" },
    { value: "IN_APPOINTMENT", label: "In Appointment", color: "blue" },
    { value: "ON_BREAK", label: "On Break", color: "orange" },
    { value: "OFF_DUTY", label: "Off Duty", color: "gray" },
    { value: "AWAY", label: "Away", color: "red" },
  ],

  appointmentTypes: [
    { value: "PRODUCT_CONSULTATION", label: "Product Consultation" },
    { value: "CUSTOM_DESIGN", label: "Custom Design" },
    { value: "REPAIR_CONSULTATION", label: "Repair Consultation" },
    { value: "APPRAISAL", label: "Appraisal" },
    { value: "ENGAGEMENT_RING", label: "Engagement Ring" },
    { value: "WEDDING_JEWELRY", label: "Wedding Jewelry" },
    { value: "GENERAL_INQUIRY", label: "General Inquiry" },
    { value: "VIP_CLIENT", label: "VIP Client" },
    { value: "COLLECTION_VIEWING", label: "Collection Viewing" },
  ],

  specializations: [
    { value: "DIAMONDS", label: "Diamonds" },
    { value: "GOLD_JEWELRY", label: "Gold Jewelry" },
    { value: "CUSTOM_DESIGN", label: "Custom Design" },
    { value: "ANTIQUE_JEWELRY", label: "Antique Jewelry" },
    { value: "GEMSTONES", label: "Gemstones" },
    { value: "WEDDING_COLLECTIONS", label: "Wedding Collections" },
    { value: "REPAIRS", label: "Repairs" },
    { value: "VALUATION", label: "Valuation" },
  ],

  timeOffTypes: [
    { value: "VACATION", label: "Vacation" },
    { value: "SICK_LEAVE", label: "Sick Leave" },
    { value: "PERSONAL", label: "Personal" },
    { value: "TRAINING", label: "Training" },
    { value: "OTHER", label: "Other" },
  ],

  accessLevels: [
    { value: "VIEW", label: "View Only", description: "Can only view data" },
    { value: "MANAGE", label: "Manage", description: "Can view and edit data" },
    {
      value: "FULL_CONTROL",
      label: "Full Control",
      description: "Complete access including delete",
    },
  ],

  rolePresets: [
    { value: "viewer", label: "Viewer - Read Only Access" },
    { value: "staff", label: "Staff - Basic Operations" },
    { value: "manager", label: "Manager - Branch Operations" },
    { value: "admin", label: "Admin - Full Access" },
    { value: "custom", label: "Custom Permissions" },
  ],

  weekDays: [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ],
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setAdminFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearAdminFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    updateSetupStep: (state, action) => {
      const { step, completed } = action.payload;
      state.setupSteps = state.setupSteps.map((s) =>
        s.step === step ? { ...s, completed } : s
      );
    },
    setSelectedSlotDate: (state, action) => {
      state.selectedSlotDate = action.payload;
    },
    setMyCurrentStatus: (state, action) => {
      state.myCurrentStatus = action.payload;
    },
    resetCurrentAdmin: (state) => {
      state.currentAdmin = null;
      state.myCurrentStatus = null;
      state.myAvailability = null;
      state.appointmentSettings = null;
      state.requiresInitialSetup = false;
      state.setupSteps = [];
      state.setupStatus = null;
    },
    clearBranchAccessState: (state) => {
      state.selectedAdminBranchAccess = null;
      state.branchAdminsList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // ========================================
      // ACTIONS
      // ========================================
      .addCase(clearAdminError, (state) => {
        state.error = null;
      })
      .addCase(resetSelectedAdmin, (state) => {
        state.selectedAdmin = null;
      })
      .addCase(setAdminFilter, (state, action) => {
        state.filters = { ...state.filters, ...action.payload };
      })
      .addCase(clearAvailabilityData, (state) => {
        state.myAvailability = null;
        state.workingHours = null;
        state.timeOffList = [];
        state.specialHoursList = [];
      })
      .addCase(clearSlotData, (state) => {
        state.availableSlots = [];
        state.availableSlotsRange = {};
        state.selectedSlotDate = null;
      })
      .addCase(clearBranchAccessData, (state) => {
        state.selectedAdminBranchAccess = null;
        state.branchAdminsList = [];
      })

      // ========================================
      // ADMIN MANAGEMENT
      // ========================================
      .addCase(getAdminThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAdmin = action.payload;
      })
      .addCase(getAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getMyDetailsThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(getMyDetailsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const adminData = action.payload.admin || action.payload;
        state.currentAdmin = adminData;
        state.requiresInitialSetup =
          action.payload.requiresInitialSetup || false;
        state.setupStatus = action.payload.systemStatus || null;
        state.setupSteps = action.payload.setupSteps || [];

        if (adminData?.currentStatus) {
          state.myCurrentStatus = adminData.currentStatus;
        }

        if (adminData?.availability?.acceptsAppointments !== undefined) {
          state.acceptingAppointments =
            adminData.availability.acceptsAppointments;
        }
      })
      .addCase(getMyDetailsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createAdminThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        const newAdmin = action.payload.data || action.payload;
        state.admins = [...state.admins, newAdmin];
        state.successMessage =
          action.payload.message || "Admin created successfully";
      })
      .addCase(createAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteAdminThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = state.admins.filter(
          (admin) => admin.adminCode !== action.meta.arg
        );
        state.successMessage = "Admin deleted successfully";
      })
      .addCase(deleteAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateAdminPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminPasswordThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.forgotPasswordRequests = state.forgotPasswordRequests.filter(
          (admin) => admin.adminCode !== action.meta.arg.adminCode
        );
        state.successMessage = "Password updated successfully";
      })
      .addCase(updateAdminPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(resetPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = "Password reset successfully";
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(requestPasswordResetThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestPasswordResetThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.displayMessage || action.payload.message;
      })
      .addCase(requestPasswordResetThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAdminsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(fetchAdminsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.admins = action.payload || [];
      })
      .addCase(fetchAdminsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(toggleBlockAdminThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleBlockAdminThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAdmin = action.payload.admin;
        if (updatedAdmin) {
          state.admins = state.admins.map((admin) =>
            admin._id === updatedAdmin._id ||
            admin.adminCode === updatedAdmin.adminCode
              ? updatedAdmin
              : admin
          );
        }
        state.successMessage = action.payload.message;
      })
      .addCase(toggleBlockAdminThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getForgotPasswordRequestsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getForgotPasswordRequestsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.forgotPasswordRequests = action.payload || [];
      })
      .addCase(getForgotPasswordRequestsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(completeInitialSetupThunk.pending, (state) => {
        state.loadingSetup = true;
        state.error = null;
      })
      .addCase(completeInitialSetupThunk.fulfilled, (state) => {
        state.loadingSetup = false;
        state.requiresInitialSetup = false;
        state.successMessage = "Initial setup completed successfully";
        if (state.currentAdmin) {
          state.currentAdmin = {
            ...state.currentAdmin,
            hasCompletedSetup: true,
          };
        }
      })
      .addCase(completeInitialSetupThunk.rejected, (state, action) => {
        state.loadingSetup = false;
        state.error = action.payload;
      })

      .addCase(updateAdminBranchAccessThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminBranchAccessThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAdmin = action.payload.admin || action.payload;
        if (updatedAdmin) {
          state.admins = state.admins.map((admin) =>
            admin._id === updatedAdmin._id ||
            admin.adminCode === updatedAdmin.adminCode
              ? updatedAdmin
              : admin
          );
        }
        state.successMessage = "Branch access updated successfully";
      })
      .addCase(updateAdminBranchAccessThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================
      // STATUS MANAGEMENT
      // ========================================
      .addCase(getAdminStatusThunk.pending, (state) => {
        state.loadingStatus = true;
        state.error = null;
      })
      .addCase(getAdminStatusThunk.fulfilled, (state, action) => {
        state.loadingStatus = false;
        const { adminCode } = action.payload;
        if (adminCode) {
          state.adminStatusMap[adminCode] = action.payload;
        }
      })
      .addCase(getAdminStatusThunk.rejected, (state, action) => {
        state.loadingStatus = false;
        state.error = action.payload;
      })

      .addCase(updateMyStatusThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMyStatusThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.myCurrentStatus =
          action.payload.currentStatus || action.payload.status || null;
        state.successMessage = "Status updated successfully";

        if (state.currentAdmin && state.myCurrentStatus) {
          state.currentAdmin = {
            ...state.currentAdmin,
            currentStatus: state.myCurrentStatus,
          };
        }
      })
      .addCase(updateMyStatusThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getAvailableAdminsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAvailableAdminsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.availableAdmins = action.payload.admins || [];
      })
      .addCase(getAvailableAdminsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // ========================================
      // AVAILABILITY MANAGEMENT
      // ========================================
      .addCase(getMyAvailabilityThunk.pending, (state) => {
        state.loadingAvailability = true;
        state.error = null;
      })
      .addCase(getMyAvailabilityThunk.fulfilled, (state, action) => {
        state.loadingAvailability = false;
        const availability = action.payload.availability || {};
        state.myAvailability = availability;
        state.appointmentSettings = action.payload.appointmentSettings || null;
        state.workingHours = availability.workingHours || null;
        state.timeOffList = availability.timeOff || [];
        state.specialHoursList = availability.specialHours || [];
        state.acceptingAppointments =
          availability.acceptsAppointments !== undefined
            ? availability.acceptsAppointments
            : true;
      })
      .addCase(getMyAvailabilityThunk.rejected, (state, action) => {
        state.loadingAvailability = false;
        state.error = action.payload;
      })

      .addCase(updateWorkingHoursThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWorkingHoursThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.workingHours = action.payload.workingHours || null;
        if (state.myAvailability) {
          state.myAvailability = {
            ...state.myAvailability,
            workingHours: action.payload.workingHours,
          };
        }
        state.successMessage = "Working hours updated successfully";
      })
      .addCase(updateWorkingHoursThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addTimeOffThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTimeOffThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.timeOffList = action.payload.timeOff || [];
        if (state.myAvailability) {
          state.myAvailability = {
            ...state.myAvailability,
            timeOff: action.payload.timeOff || [],
          };
        }
        state.successMessage = "Time off added successfully";
      })
      .addCase(addTimeOffThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeTimeOffThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeTimeOffThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.timeOffList = action.payload.timeOff || [];
        if (state.myAvailability) {
          state.myAvailability = {
            ...state.myAvailability,
            timeOff: action.payload.timeOff || [],
          };
        }
        state.successMessage = "Time off removed successfully";
      })
      .addCase(removeTimeOffThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addSpecialHoursThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSpecialHoursThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.specialHoursList = action.payload.specialHours || [];
        if (state.myAvailability) {
          state.myAvailability = {
            ...state.myAvailability,
            specialHours: action.payload.specialHours || [],
          };
        }
        state.successMessage = "Special hours added successfully";
      })
      .addCase(addSpecialHoursThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================
      // APPOINTMENT SETTINGS
      // ========================================
      .addCase(updateAppointmentSettingsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppointmentSettingsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.appointmentSettings = action.payload.appointmentSettings || null;
        state.successMessage = "Appointment settings updated successfully";
      })
      .addCase(updateAppointmentSettingsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(toggleAcceptingAppointmentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleAcceptingAppointmentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.acceptingAppointments =
          action.payload.acceptsAppointments !== undefined
            ? action.payload.acceptsAppointments
            : state.acceptingAppointments;
        if (state.myAvailability) {
          state.myAvailability = {
            ...state.myAvailability,
            acceptsAppointments: action.payload.acceptsAppointments,
          };
        }
        state.successMessage = action.payload.message;
      })
      .addCase(toggleAcceptingAppointmentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================
      // SLOT MANAGEMENT
      // ========================================
      .addCase(getAvailableSlotsThunk.pending, (state) => {
        state.loadingSlots = true;
        state.error = null;
      })
      .addCase(getAvailableSlotsThunk.fulfilled, (state, action) => {
        state.loadingSlots = false;
        state.availableSlots = action.payload.slots || [];
      })
      .addCase(getAvailableSlotsThunk.rejected, (state, action) => {
        state.loadingSlots = false;
        state.error = action.payload;
      })

      .addCase(getAvailableSlotsRangeThunk.pending, (state) => {
        state.loadingSlots = true;
        state.error = null;
      })
      .addCase(getAvailableSlotsRangeThunk.fulfilled, (state, action) => {
        state.loadingSlots = false;
        state.availableSlotsRange = action.payload.slots || {};
      })
      .addCase(getAvailableSlotsRangeThunk.rejected, (state, action) => {
        state.loadingSlots = false;
        state.error = action.payload;
      })

      // ========================================
      // NOTIFICATION SETTINGS
      // ========================================
      .addCase(updateNotificationSettingsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationSettingsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationSettings =
          action.payload.notificationSettings || null;
        state.successMessage = "Notification settings updated successfully";
      })
      .addCase(updateNotificationSettingsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================
      // SEARCH & FILTER
      // ========================================
      .addCase(getAdminsBySpecializationThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAdminsBySpecializationThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.specializedAdmins = action.payload.admins || [];
      })
      .addCase(getAdminsBySpecializationThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      // ========================================
      // INTEGRATION
      // ========================================
      .addCase(getAdminDetailsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminDetailsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAdmin = action.payload.admin || action.payload;
      })
      .addCase(getAdminDetailsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(checkAdminAvailabilityThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAdminAvailabilityThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkAdminAvailabilityThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getBranchAdminsThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getBranchAdminsThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.branchAdmins = action.payload.admins || action.payload || [];
      })
      .addCase(getBranchAdminsThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(getAdminsByRoleThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAdminsByRoleThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.admins = action.payload.admins || action.payload || [];
      })
      .addCase(getAdminsByRoleThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(getBulkAdminAvailabilityThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBulkAdminAvailabilityThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.bulkAvailabilityResults = action.payload || {};
      })
      .addCase(getBulkAdminAvailabilityThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateAdminRatingThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminRatingThunk.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = "Rating updated successfully";
      })
      .addCase(updateAdminRatingThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateAppointmentStatsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAppointmentStatsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.appointmentStats = action.payload.stats || null;
      })
      .addCase(updateAppointmentStatsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================
      // BRANCH ACCESS MANAGEMENT
      // ========================================
      .addCase(getAdminBranchAccessThunk.pending, (state) => {
        state.loadingBranchAccess = true;
        state.error = null;
      })
      .addCase(getAdminBranchAccessThunk.fulfilled, (state, action) => {
        state.loadingBranchAccess = false;
        state.selectedAdminBranchAccess = action.payload;
      })
      .addCase(getAdminBranchAccessThunk.rejected, (state, action) => {
        state.loadingBranchAccess = false;
        state.error = action.payload;
      })

      .addCase(addBranchAccessThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addBranchAccessThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update selected admin's branch access
        if (
          state.selectedAdminBranchAccess?.adminCode ===
          action.payload.adminCode
        ) {
          state.selectedAdminBranchAccess = action.payload;
        }
        // Update admin in admins list
        const updatedAdmin = action.payload.admin;
        if (updatedAdmin) {
          state.admins = state.admins.map((admin) =>
            admin.adminCode === updatedAdmin.adminCode ? updatedAdmin : admin
          );
        }
        state.successMessage =
          action.payload.message || "Branch access added successfully";
      })
      .addCase(addBranchAccessThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateBranchAccessPermissionsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateBranchAccessPermissionsThunk.fulfilled,
        (state, action) => {
          state.loading = false;
          // Update selected admin's branch access
          if (
            state.selectedAdminBranchAccess?.adminCode ===
            action.payload.adminCode
          ) {
            state.selectedAdminBranchAccess = action.payload;
          }
          state.successMessage =
            action.payload.message || "Branch access updated successfully";
        }
      )
      .addCase(updateBranchAccessPermissionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeBranchAccessThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeBranchAccessThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update selected admin's branch access
        if (
          state.selectedAdminBranchAccess?.adminCode ===
          action.payload.adminCode
        ) {
          state.selectedAdminBranchAccess = action.payload;
        }
        state.successMessage =
          action.payload.message || "Branch access removed successfully";
      })
      .addCase(removeBranchAccessThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(setAdminPrimaryBranchThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setAdminPrimaryBranchThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update selected admin's branch access
        if (
          state.selectedAdminBranchAccess?.adminCode ===
          action.payload.adminCode
        ) {
          state.selectedAdminBranchAccess = action.payload;
        }
        // Update admin in admins list
        const updatedAdmin = action.payload.admin;
        if (updatedAdmin) {
          state.admins = state.admins.map((admin) =>
            admin.adminCode === updatedAdmin.adminCode ? updatedAdmin : admin
          );
        }
        state.successMessage =
          action.payload.message || "Primary branch updated successfully";
      })
      .addCase(setAdminPrimaryBranchThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getAdminsByBranchThunk.pending, (state) => {
        state.loadingList = true;
        state.error = null;
      })
      .addCase(getAdminsByBranchThunk.fulfilled, (state, action) => {
        state.loadingList = false;
        state.branchAdminsList = action.payload.admins || [];
      })
      .addCase(getAdminsByBranchThunk.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload;
      })

      .addCase(bulkAssignBranchAccessThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkAssignBranchAccessThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.message || "Bulk branch access assigned successfully";
      })
      .addCase(bulkAssignBranchAccessThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setAdminFilters,
  clearAdminFilters,
  clearSuccessMessage,
  updateSetupStep,
  setSelectedSlotDate,
  setMyCurrentStatus,
  resetCurrentAdmin,
  clearBranchAccessState,
} = adminSlice.actions;

export default adminSlice.reducer;
